"use client";

import { useRef, useState } from "react";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { NetworkFigure } from "@/components/tools/physics-waves/network-view";
import { PhysicsPlot } from "@/components/tools/physics-waves/plot";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import {
  RcPanel,
  type RcStrings,
} from "@/components/tools/physics-waves/rc-panel";
import {
  RowList,
  type ValueRow,
} from "@/components/tools/physics-waves/row-list";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  analyseBank,
  CAP_PAIRS,
  type CapValues,
  type CapVariable,
  parallelCapacitance,
  seriesCapacitance,
  solveCapacitor,
} from "@/lib/tool-kit/physics-waves/capacitor";
import {
  axisPrefix,
  type NumberFormatter,
  shiftExp,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import { listNetwork } from "@/lib/tool-kit/physics-waves/resistor-network";
import {
  COULOMB_UNITS,
  FARAD_UNITS,
  findUnit,
  JOULE_UNITS,
  toBase,
  type UnitDef,
  VOLT_UNITS,
} from "@/lib/tool-kit/physics-waves/units";

/** English strings. A translated content file overrides these through `ui`. */
export const CAPACITOR_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modes: {
    series: "Capacitors in series",
    parallel: "Capacitors in parallel",
    charge: "Charge and energy (Q = CV)",
    rc: "RC charge and discharge",
  },
  rowLabel: "Capacitor C{n}",
  unitFor: "Unit for {name}",
  remove: "Remove C{n}",
  add: "Add a capacitor",
  supplyLabel: "Voltage across the bank (optional)",
  supplyHint:
    "Add a voltage to see the charge, voltage and energy of each capacitor.",
  sigFigs: "Round to this many significant figures",
  results: "Results",
  enterTwo: "Enter at least two capacitances to combine.",
  enterAll: "Enter a number in every box to see the result.",
  needPositive: "Every value must be a number above zero.",
  equivalent: "Equivalent capacitance",
  count: "Capacitors",
  totalCharge: "Total charge",
  totalEnergy: "Total energy",
  working: "The working",
  seriesStep: "1 ÷ ({parts}) = {result}",
  parallelStep: "{parts} = {result}",
  loadsHeading: "Each capacitor with this voltage",
  columnCapacitor: "Capacitor",
  columnCapacitance: "Capacitance",
  columnVoltage: "Voltage across",
  columnCharge: "Charge",
  columnEnergy: "Energy",
  capacitorName: "C{n}",
  figureLabel: "Schematic of the capacitor bank",
  figureDescription:
    "The capacitors drawn as a circuit with an equivalent capacitance of {total}.",
  seriesNote:
    "In series every capacitor holds the same charge, so the smallest capacitance takes the largest voltage. Check each capacitor's voltage rating against its share.",
  pairLabel: "Which two quantities do you know?",
  pairs: {
    CV: "Capacitance and voltage",
    CQ: "Capacitance and charge",
    VQ: "Voltage and charge",
    CE: "Capacitance and energy",
    VE: "Voltage and energy",
    QE: "Charge and energy",
  },
  names: {
    C: "Capacitance (C)",
    V: "Voltage (V)",
    Q: "Charge (Q)",
    E: "Energy (E)",
  },
  solved: "Calculated",
  known: "You entered",
  chargeGraphHeading: "Charge against voltage",
  chargeGraphLabel: "Q–V graph for a {capacitance} capacitor",
  chargeGraphDescription:
    "A straight line through the origin whose slope is the capacitance, with a dot at {voltage} and {charge}.",
  chargeGraphX: "Voltage ({unit})",
  chargeGraphY: "Charge ({unit})",
  chargeGraphNote:
    "The slope is C, and the area under the line up to the dot is the stored energy, ½QV.",
  chargeStepQ: "Q = C × V = {c} × {v} = {q}",
  chargeStepE: "E = ½ × C × V² = ½ × {c} × ({v})² = {e}",
  chargeStepV: "V = Q ÷ C = {q} ÷ {c} = {v}",
  chargeStepEq: "E = Q² ÷ (2C) = ({q})² ÷ (2 × {c}) = {e}",
  chargeStepC: "C = Q ÷ V = {q} ÷ {v} = {c}",
  chargeStepEqv: "E = ½ × Q × V = ½ × {q} × {v} = {e}",
  chargeStepVe: "V = √(2E ÷ C) = √(2 × {e} ÷ {c}) = {v}",
  chargeStepQe: "Q = C × V = {c} × {v} = {q}",
  chargeStepCe: "C = 2E ÷ V² = 2 × {e} ÷ ({v})² = {c}",
  chargeStepQev: "Q = 2E ÷ V = 2 × {e} ÷ {v} = {q}",
  chargeStepCqe: "C = Q² ÷ (2E) = ({q})² ÷ (2 × {e}) = {c}",
  chargeStepVqe: "V = 2E ÷ Q = 2 × {e} ÷ {q} = {v}",
  rcResistance: "Resistance (R)",
  rcCapacitance: "Capacitance (C)",
  rcSource: "Source voltage",
  rcTime: "Time since the switch closed",
  rcDirection: "Which way is the capacitor going?",
  rcCharging: "Charging from 0 V",
  rcDischarging: "Discharging from the source voltage",
  rcTau: "Time constant (τ = RC)",
  rcCorner: "Corner frequency 1 ÷ (2πRC)",
  rcVoltageAt: "Capacitor voltage at that time",
  rcCurrentAt: "Current at that time",
  rcPercent: "Share of the source voltage",
  rcMilestones: "Milestones",
  rcColumnWhen: "Point",
  rcColumnTime: "Time",
  rcColumnVoltage: "Capacitor voltage",
  rcColumnPercent: "Share of the source",
  rcTauRow: "{n} × τ",
  rcHalfRow: "50% of the change",
  rcNinetyRow: "90% of the change",
  rcNinetyNineRow: "99% of the change",
  rcStepTau: "τ = R × C = {r} × {c} = {tau}",
  rcStepVoltageCharge:
    "v = V × (1 − e^(−t/τ)) = {source} × (1 − e^(−{time}/{tau})) = {vc}",
  rcStepVoltageDischarge:
    "v = V × e^(−t/τ) = {source} × e^(−{time}/{tau}) = {vc}",
  rcStepCurrent:
    "i = (V ÷ R) × e^(−t/τ) = ({source} ÷ {r}) × e^(−{time}/{tau}) = {current}",
  rcStepCorner: "fc = 1 ÷ (2π × R × C) = 1 ÷ (2π × {r} × {c}) = {fc}",
  rcGraphLabel: "RC charging and discharging curve",
  rcGraphDescription:
    "Capacitor voltage over five time constants, where one time constant is {tau}.",
  rcGraphX: "Time ({unit})",
  rcGraphY: "Voltage ({unit})",
  rcTimeBeyond:
    "That time is beyond five time constants, so the curve above stops before it; the numbers still apply.",
  rcOneTau: "1 τ",
} as const;

type Strings = typeof CAPACITOR_CALCULATOR_STRINGS;
type Mode = keyof Strings["modes"];
type PairKey = keyof Strings["pairs"];

const SIG_CHOICES = ["3", "4", "5"] as const;

const VARIABLE_UNITS: Record<CapVariable, UnitDef[]> = {
  C: FARAD_UNITS,
  V: VOLT_UNITS,
  Q: COULOMB_UNITS,
  E: JOULE_UNITS,
};
const SYMBOLS: Record<CapVariable, string> = { C: "F", V: "V", Q: "C", E: "J" };
const CAP_VARIABLES: readonly CapVariable[] = ["C", "V", "Q", "E"];

interface Entry {
  value: string;
  unit: string;
}

const INITIAL_ENTRIES: Record<CapVariable, Entry> = {
  C: { value: "100", unit: "uF" },
  V: { value: "12", unit: "V" },
  Q: { value: "500", unit: "uC" },
  E: { value: "2", unit: "mJ" },
};

const INITIAL_SERIES: ValueRow[] = [
  { id: 1, value: "10", unit: "uF" },
  { id: 2, value: "20", unit: "uF" },
];
const INITIAL_PARALLEL: ValueRow[] = [
  { id: 3, value: "1", unit: "uF" },
  { id: 4, value: "2.2", unit: "uF" },
  { id: 5, value: "4.7", unit: "uF" },
];

function showCap(
  fmt: NumberFormatter,
  variable: CapVariable,
  value: number,
  sig: number
): string {
  return withSiPrefix(fmt, value, SYMBOLS[variable], sig);
}

/** The working lines for the two unknowns of a pair, with the numbers put in. */
function chargeLines(
  pair: readonly [CapVariable, CapVariable],
  v: CapValues,
  strings: Strings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const text = {
    c: showCap(fmt, "C", v.C, sig),
    v: showCap(fmt, "V", v.V, sig),
    q: showCap(fmt, "Q", v.Q, sig),
    e: showCap(fmt, "E", v.E, sig),
  };
  const key = [...pair].sort().join("");
  if (key === "CV") {
    return [t(strings.chargeStepQ, text), t(strings.chargeStepE, text)];
  }
  if (key === "CQ") {
    return [t(strings.chargeStepV, text), t(strings.chargeStepEq, text)];
  }
  if (key === "QV") {
    return [t(strings.chargeStepC, text), t(strings.chargeStepEqv, text)];
  }
  if (key === "CE") {
    return [t(strings.chargeStepVe, text), t(strings.chargeStepQe, text)];
  }
  if (key === "EV") {
    return [t(strings.chargeStepCe, text), t(strings.chargeStepQev, text)];
  }
  return [t(strings.chargeStepCqe, text), t(strings.chargeStepVqe, text)];
}

function ChargeGraph({
  values,
  strings,
  fmt,
  sig,
}: {
  values: CapValues;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const vMax = values.V * 1.25;
  const xPrefix = axisPrefix(vMax);
  const yPrefix = axisPrefix(values.C * vMax);
  const toX = (x: number) => shiftExp(x, -xPrefix.exp);
  const toY = (y: number) => shiftExp(y, -yPrefix.exp);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.chargeGraphHeading}</h3>
      <PhysicsPlot
        curves={[
          {
            points: [
              [0, 0],
              [toX(vMax), toY(values.C * vMax)],
            ],
          },
        ]}
        description={t(strings.chargeGraphDescription, {
          voltage: showCap(fmt, "V", values.V, sig),
          charge: showCap(fmt, "Q", values.Q, sig),
        })}
        dots={[
          {
            x: toX(values.V),
            y: toY(values.Q),
            label: `${showCap(fmt, "V", values.V, sig)}, ${showCap(fmt, "Q", values.Q, sig)}`,
          },
        ]}
        formatX={(v) => fmt(v, { maximumFractionDigits: 6 })}
        formatY={(v) => fmt(v, { maximumFractionDigits: 6 })}
        id="cap-q-graph"
        label={t(strings.chargeGraphLabel, {
          capacitance: showCap(fmt, "C", values.C, sig),
        })}
        xRange={[0, toX(vMax)]}
        xTitle={t(strings.chargeGraphX, { unit: `${xPrefix.symbol}V` })}
        yRange={[0, toY(values.C * vMax)]}
        yTitle={t(strings.chargeGraphY, { unit: `${yPrefix.symbol}C` })}
      />
      <p className="text-muted-foreground text-xs">{strings.chargeGraphNote}</p>
    </div>
  );
}

export function ChargePanel({
  strings,
  fmt,
  sig,
}: {
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [pairKey, setPairKey] = useState<PairKey>("CV");
  const [entries, setEntries] = useState(INITIAL_ENTRIES);
  const pair = [pairKey[0], pairKey[1]] as [CapVariable, CapVariable];
  const known: Partial<Record<CapVariable, number>> = {};
  for (const variable of pair) {
    const parsed = parseNumberInput(entries[variable].value);
    if (parsed !== null) {
      known[variable] = toBase(
        parsed,
        findUnit(VARIABLE_UNITS[variable], entries[variable].unit)
      );
    }
  }
  const outcome = solveCapacitor(pair[0], pair[1], known);
  const setEntry = (variable: CapVariable, patch: Partial<Entry>) =>
    setEntries((current) => ({
      ...current,
      [variable]: { ...current[variable], ...patch },
    }));

  return (
    <>
      <PillGroup
        label={strings.pairLabel}
        name="cap-pair"
        onChange={setPairKey}
        options={CAP_PAIRS.map(([a, b]) => ({
          value: `${a}${b}` as PairKey,
          label: strings.pairs[`${a}${b}` as PairKey],
        }))}
        value={pairKey}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {pair.map((variable) => (
          <QuantityField
            id={`cap-${variable}`}
            key={variable}
            label={strings.names[variable]}
            onChange={(value) => setEntry(variable, { value })}
            onUnitChange={(unit) => setEntry(variable, { unit })}
            unit={entries[variable].unit}
            unitLabel={t(strings.unitFor, { name: strings.names[variable] })}
            units={VARIABLE_UNITS[variable]}
            value={entries[variable].value}
          />
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <>
            <StatGrid>
              {CAP_VARIABLES.map((variable) => (
                <StatCard
                  emphasis={!pair.includes(variable)}
                  key={variable}
                  label={`${strings.names[variable]} — ${pair.includes(variable) ? strings.known : strings.solved}`}
                  value={showCap(fmt, variable, outcome.values[variable], sig)}
                />
              ))}
            </StatGrid>
            <WorkingSteps
              heading={strings.working}
              lines={chargeLines(pair, outcome.values, strings, fmt, sig)}
            />
            <ChargeGraph
              fmt={fmt}
              sig={sig}
              strings={strings}
              values={outcome.values}
            />
          </>
        ) : null}
        {!outcome.ok && outcome.error === "need-two" ? (
          <Notice>{strings.enterAll}</Notice>
        ) : null}
        {!outcome.ok && outcome.error === "need-positive" ? (
          <ErrorNotice>{strings.needPositive}</ErrorNotice>
        ) : null}
      </section>
    </>
  );
}

function BankResults({
  kind,
  farads,
  supply,
  strings,
  fmt,
  sig,
}: {
  kind: "series" | "parallel";
  farads: number[];
  supply: number | null;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const total =
    kind === "series" ? seriesCapacitance(farads) : parallelCapacitance(farads);
  const farad = (value: number) => withSiPrefix(fmt, value, "F", sig);
  const parts = farads.map(farad);
  const bank = supply === null ? [] : analyseBank(kind, farads, supply);
  const network = listNetwork(kind, farads);
  const working =
    kind === "series"
      ? t(strings.seriesStep, {
          parts: farads.map((f) => `1/${farad(f)}`).join(" + "),
          result: farad(total),
        })
      : t(strings.parallelStep, {
          parts: parts.join(" + "),
          result: farad(total),
        });
  return (
    <div className="space-y-4">
      <StatGrid>
        <StatCard emphasis label={strings.equivalent} value={farad(total)} />
        <StatCard label={strings.count} value={fmt(farads.length)} />
        {supply === null ? null : (
          <>
            <StatCard
              label={strings.totalCharge}
              value={withSiPrefix(fmt, total * supply, "C", sig)}
            />
            <StatCard
              label={strings.totalEnergy}
              value={withSiPrefix(fmt, 0.5 * total * supply * supply, "J", sig)}
            />
          </>
        )}
      </StatGrid>
      {network === null ? null : (
        <NetworkFigure
          description={t(strings.figureDescription, { total: farad(total) })}
          figureId="cap-figure"
          fmt={fmt}
          label={strings.figureLabel}
          network={network}
          sig={sig}
          symbol="capacitor"
          tag={strings.capacitorName}
          unit="F"
        />
      )}
      <WorkingSteps heading={strings.working} lines={[working]} />
      {kind === "series" ? <Notice>{strings.seriesNote}</Notice> : null}
      {bank.length > 0 ? (
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.loadsHeading}</h3>
          <DataTable
            head={[
              strings.columnCapacitor,
              strings.columnCapacitance,
              strings.columnVoltage,
              strings.columnCharge,
              strings.columnEnergy,
            ]}
            rows={bank.map((cap, i) => [
              t(strings.capacitorName, { n: i + 1 }),
              farad(cap.farads),
              withSiPrefix(fmt, cap.volts, "V", sig),
              withSiPrefix(fmt, cap.coulombs, "C", sig),
              withSiPrefix(fmt, cap.joules, "J", sig),
            ])}
          />
        </div>
      ) : null}
    </div>
  );
}

function readBank(rows: ValueRow[]): number[] | "positive" | null {
  const values: number[] = [];
  for (const row of rows) {
    if (row.value.trim() === "") {
      continue;
    }
    const parsed = parseNumberInput(row.value);
    if (parsed === null || parsed <= 0) {
      return "positive";
    }
    values.push(toBase(parsed, findUnit(FARAD_UNITS, row.unit)));
  }
  return values.length < 2 ? null : values;
}

export default function CapacitorCalculatorWidget() {
  const strings = useToolUi(CAPACITOR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("series");
  const [seriesRows, setSeriesRows] = useState(INITIAL_SERIES);
  const [parallelRows, setParallelRows] = useState(INITIAL_PARALLEL);
  const [supply, setSupply] = useState("12");
  const [supplyUnit, setSupplyUnit] = useState("V");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const nextId = useRef(100);
  const fresh = () => {
    nextId.current += 1;
    return nextId.current;
  };
  const sig = Number(sigText);
  const isBank = mode === "series" || mode === "parallel";
  const rows = mode === "series" ? seriesRows : parallelRows;
  const bank = isBank ? readBank(rows) : null;
  const supplyValue = parseNumberInput(supply);
  const volts =
    supplyValue === null || supplyValue <= 0
      ? null
      : toBase(supplyValue, findUnit(VOLT_UNITS, supplyUnit));
  const rowStrings = {
    rowLabel: strings.rowLabel,
    unitFor: strings.unitFor,
    remove: strings.remove,
    add: strings.add,
  };
  const rcStrings: RcStrings = strings;

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="cap-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      {isBank ? (
        <>
          <RowList
            defaultUnit="uF"
            idPrefix={`cap-${mode}`}
            nextId={fresh}
            onChange={mode === "series" ? setSeriesRows : setParallelRows}
            rows={rows}
            strings={rowStrings}
            units={FARAD_UNITS}
          />
          <QuantityField
            hint={strings.supplyHint}
            id="cap-supply"
            label={strings.supplyLabel}
            onChange={setSupply}
            onUnitChange={setSupplyUnit}
            unit={supplyUnit}
            unitLabel={t(strings.unitFor, { name: strings.supplyLabel })}
            units={VOLT_UNITS}
            value={supply}
          />
        </>
      ) : null}
      <div className="max-w-xs">
        <SelectField
          id="cap-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      {isBank ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {Array.isArray(bank) ? (
            <BankResults
              farads={bank}
              fmt={fmt}
              kind={mode === "series" ? "series" : "parallel"}
              sig={sig}
              strings={strings}
              supply={volts}
            />
          ) : null}
          {bank === null ? <Notice>{strings.enterTwo}</Notice> : null}
          {bank === "positive" ? (
            <ErrorNotice>{strings.needPositive}</ErrorNotice>
          ) : null}
        </section>
      ) : null}
      {mode === "charge" ? (
        <ChargePanel fmt={fmt} sig={sig} strings={strings} />
      ) : null}
      {mode === "rc" ? (
        <RcPanel
          fmt={fmt}
          heading={strings.results}
          sig={sig}
          strings={rcStrings}
        />
      ) : null}
    </>
  );
}
